// === src/components/Bxinventar/Edit.jsx ===
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, Alert, Button, Badge, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../axiosConfig";
import { BxinventarForm } from "./Form";

// normalize ca intrare (pt dirty detect) :contentReference[oaicite:2]{index=2}
function normalizeBxinventar(m) {
  if (!m) return null;
  const { id, created_at, updated_at, user_id, linii = [], ...rest } = m;

  const normLinii = [...linii]
    .map(({ id, created_at, updated_at, _tmp, ...ln }) => ln)
    .sort((a, b) => {
      const ka = a.bxidlns ?? a.bxpart_id ?? 0;
      const kb = b.bxidlns ?? b.bxpart_id ?? 0;
      return ka === kb ? 0 : ka < kb ? -1 : 1;
    });

  return { ...rest, linii: normLinii };
}
const hash = (obj) => JSON.stringify(obj);

export default function BxinventarEdit() {
  const { id } = useParams();

  const [form, setForm] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // load
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(`/bxinventar/${id}`);
        setForm(data);
        setInitialForm(data);
      } catch (e) {
        setError(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // dirty
  const originalHash = useMemo(
    () => hash(normalizeBxinventar(initialForm)),
    [initialForm],
  );
  const currentHash = useMemo(() => hash(normalizeBxinventar(form)), [form]);
  const isDirty = originalHash !== currentHash;

  const statusText = isDirty ? "Modificat (nesalvat)" : "Salvat";
  const statusVariant = isDirty ? "warning" : "success";

  // warn on unload
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put(`/bxinventar/${id}`, form);
      toast.success("Inventarul a fost salvat cu succes!");
      // după salvare: snapshot update ca să devină "Salvat" (exact ca intrare) :contentReference[oaicite:3]{index=3}
      setForm(data);
      setInitialForm(data);
    } catch (e) {
      setError(String(e.message || e));
      toast.error(`Eroare la salvare: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Container className="py-3">Se încarcă…</Container>;
  if (!form)
    return (
      <Container className="py-3 text-danger">
        Nu s-a găsit înregistrarea.
      </Container>
    );

  return (
    <Container className="py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <h3 className="mb-0">Editează inventar #{id}</h3>
          <Badge bg={statusVariant}>{statusText}</Badge>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="primary"
            disabled={!isDirty || saving}
            onClick={save}
          >
            {saving && <Spinner size="sm" className="me-2" />}Salvează
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <BxinventarForm
        value={form}
        onChange={setForm}
        onSubmit={save}
        submitting={saving}
      />
    </Container>
  );
}
